import {
    api,
    ApiError,
    apiErrorMessage,
    codeLabel,
    paymentSystemLabel,
    useAuthStore,
} from '@bemor/web-kit';
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    DateTime,
    EditIcon,
    Field,
    InfoIcon,
    Input,
    KeyValueList,
    ListTable,
    PageHeader,
    PaymentSystemIcon,
    SearchableMultiSelect,
    SearchableSelect,
    Separator,
    Switch,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    type SelectOption,
} from '@bemor/web-kit/ui';
import {
    useEffect,
    useRef,
    useState,
    type FormEvent,
    type ReactNode,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type {
    Merchant,
    MerchantPaymentSystem,
    PaymentSystemCatalogue,
    PaymentSystemOffer,
    PaymentSystemStage,
    Provider,
    ProviderAccount,
    ProviderAccountBinding,
    ProviderAccountRequest,
    ProviderAccountUpdateRequest,
    ProviderAccountUsage,
    TenantProvider,
    TenantProviderUpdateRequest,
} from '@/api/types';
import { DetailBody } from '@/components/detail';
import {
    merchantOption,
    SearchError,
    useMerchantSearch,
} from '@/lib/merchant-search';
import { offerText } from '@/lib/offer';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useList, useOne, useWrite } from '@/lib/queries';

const BASE = '/staff/payment-systems';
const ACCOUNTS = '/staff/provider-accounts';
/** The tenant's own setup of a provider (BM-70 § 7a): on/off, default endpoint, allow-list. */
const tenantProviderPath = (provider: string) =>
    `/staff/tenant-providers/${encodeURIComponent(provider)}`;
/** Who uses each account (BM-70 V-3, Q3): every live binding; a link of no Merchant serves nobody (D-49). */
const BINDINGS = `${ACCOUNTS}/bindings`;

/** The two refusals of a verify-first save (BM-70 § 7, D-51), in the § 7b words. */
const VERIFY_WORDS: Record<string, string> = {
    provider_unreachable:
        'NMI could not be reached — nothing was changed, try again',
    verification_failed: 'NMI refused the keys — nothing was changed',
};

/** An account a tenant default endpoint change did not verify at (the 422's `accounts`, D-53). */
type FailedAccount = { public_id: number; name: string; reason: string };

/** A refused save: its words, the input it names (`field`) and, for a tenant default endpoint, the failed accounts. */
type Refusal = {
    message: string;
    field: string | null;
    accounts: readonly FailedAccount[];
};

function refusalOf(e: unknown): Refusal {
    const item = ApiError.is(e) ? e.errors[0] : undefined;
    return {
        message: (item && VERIFY_WORDS[item.key]) ?? apiErrorMessage(e),
        field: item?.field ?? null,
        accounts: Array.isArray(item?.accounts)
            ? (item.accounts as FailedAccount[])
            : [],
    };
}

/** The endpoint picker a refusal's field names: `base_url` / `host` on the tenant default, `config.*` on an account. */
const endpointKeyOf = (field: string | null): EndpointKey | null => {
    const key = field?.replace(/^config\./, '');
    return key === 'base_url' || key === 'host' ? key : null;
};
type Stage = PaymentSystemStage['stage'];
const STAGES: readonly Stage[] = ['integrating', 'testing', 'live', 'disabled'];
type Catalogue = PaymentSystemCatalogue;

/** The two stages that change who checkout offers the system to, so they are confirmed first (spec § 9). */
const CONFIRM_COPY: Partial<Record<Stage, string>> = {
    disabled: 'Checkout stops offering this payment system to every Merchant.',
    live: 'Checkout offers this payment system to every Merchant it is enabled for.',
};

const STAGE_TONE = {
    live: 'success',
    testing: 'info',
    integrating: 'neutral',
    disabled: 'danger',
} as const satisfies Record<Stage, string>;

const COUNTRIES_ERROR = 'Enter two-letter country codes separated by commas.';

/** A comma-separated text as a list: items trimmed, empty ones dropped, each kept once. */
const splitList = (text: string, upper = false) => [
    ...new Set(
        text
            .split(',')
            .map((s) => (upper ? s.trim().toUpperCase() : s.trim()))
            .filter(Boolean),
    ),
];

/** The country input as `allowed_countries`: null when empty (never `[]`), else two-letter codes; undefined when refused. */
function parseCountries(text: string): string[] | null | undefined {
    const codes = splitList(text, true);
    if (codes.some((c) => !/^[A-Z]{2}$/.test(c))) return undefined;
    return codes.length ? codes : null;
}

/** The payment systems a provider carries: the tenant's stage rows of that provider (T-70). */
const stagesOf = (c: Catalogue, provider: string) =>
    c.stages.filter((r) => r.provider === provider);

/** The account an id names in the pool, else its bare number (an account the pool did not list). */
const accountOf = (pool: readonly ProviderAccount[], id: number) =>
    pool.find((a) => a.id === id);

/** The admin routes of § 7b: the Providers list, a provider's detail and an account's detail. */
const LIST_PAGE = '/settings/payment-systems';
const providerPage = (code: string) =>
    `${LIST_PAGE}/${encodeURIComponent(code)}`;
const accountPage = (code: string, id: number) =>
    `${providerPage(code)}/accounts/${id}`;

/** The Merchants assigned to one account; a link of no Merchant serves nobody and is not shown (D-49, T-90). */
const merchantsOf = (
    usages: readonly ProviderAccountUsage[],
    accountId: number,
) =>
    usages.filter(
        (u) => u.provider_account_id === accountId && u.merchant_id !== null,
    );

/**
 * Settings › Providers (BM-70 § 7b, QA-21): the tenant's providers as a list; a row opens the provider's detail. There
 * is no tenant-default switch (BM-70 D-46): a Merchant's own account and switches are set on its page.
 */
export function PaymentSystemsPage() {
    const catalogue = useOne<Catalogue>(BASE);
    const accounts = useOne<ProviderAccount[]>(ACCOUNTS);
    const navigate = useNavigate();
    const pool = accounts.data?.data;
    return (
        <div className="space-y-4">
            <PageHeader
                title="Providers"
                meta="Each provider the tenant uses: open one to switch it on or off, set its endpoint, and manage its accounts and payment systems."
            />
            <DetailBody query={catalogue} title="Providers">
                {(c) => (
                    <ListTable<Provider>
                        rows={[...c.providers]}
                        rowKey={(p) => p.code}
                        rowProps={(p) => ({
                            onClick: () => navigate(providerPage(p.code)),
                            className: 'cursor-pointer',
                        })}
                        emptyState={
                            <p className="text-muted-foreground p-6 text-center text-sm">
                                No provider yet.
                            </p>
                        }
                        columns={[
                            {
                                key: 'name',
                                label: 'Provider',
                                cell: (p) => (
                                    <Link
                                        className="font-medium underline-offset-4 hover:underline"
                                        to={providerPage(p.code)}
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`provider-link-${p.code}`}
                                    >
                                        {p.display_name}
                                    </Link>
                                ),
                            },
                            {
                                key: 'status',
                                label: 'Status',
                                cell: (p) => (
                                    <ProviderStatus provider={p.code} />
                                ),
                            },
                            {
                                key: 'accounts',
                                label: 'Accounts',
                                cell: (p) =>
                                    pool
                                        ? String(
                                              pool.filter(
                                                  (a) => a.provider === p.code,
                                              ).length,
                                          )
                                        : '…',
                            },
                            {
                                key: 'systems',
                                label: 'Payment systems',
                                cell: (p) =>
                                    stagesOf(c, p.code)
                                        .map((r) =>
                                            paymentSystemLabel(
                                                r.payment_system,
                                            ),
                                        )
                                        .join(', ') || 'None yet',
                            },
                        ]}
                    />
                )}
            </DetailBody>
        </div>
    );
}

/** A provider row's on/off state, from the tenant's own setup of the provider (§ 7a). */
function ProviderStatus({ provider }: { provider: string }) {
    const tp = useOne<TenantProvider>(tenantProviderPath(provider)).data?.data;
    if (!tp) return <>…</>;
    return (
        <span data-testid={`provider-status-${provider}`}>
            <Badge variant={tp.enabled ? 'success' : 'neutral'}>
                {tp.enabled ? 'On' : 'Off'}
            </Badge>
        </span>
    );
}

/** BM-110 § 9 (T-27): a tab's count, '0' when its list is empty and nothing while it loads. */
function TabCount({ count }: { count: number | undefined }) {
    return count === undefined ? null : (
        <Badge variant="neutral" size="sm">
            {count}
        </Badge>
    );
}

/**
 * A provider's detail (BM-70 § 7b, QA-21): ONE card with its on/off switch and default endpoint, then the Accounts tab
 * and the Payment systems tab (each system's stage and testers only; a Merchant's systems show on its own page, D-55).
 */
export function ProviderPage() {
    const { provider = '' } = useParams();
    const catalogue = useOne<Catalogue>(BASE);
    // The tester pickers read the staff merchants list, a separate permission; without it they take a typed id.
    const canListMerchants = useAuthStore((s) => s.can)(P.merchantsRead);
    // The first page names the stored testers before any search; the pickers themselves search the server.
    const merchants = useList<Merchant>(
        '/staff/merchants',
        {},
        canListMerchants,
    );
    const known: SelectOption[] | null = canListMerchants
        ? (merchants.data?.data.items ?? []).map(merchantOption)
        : null;
    const found = catalogue.data?.data.providers.find(
        (p) => p.code === provider,
    );
    // BM-110 § 9 (T-27): the Accounts count is what its table lists (the same read), disabled accounts included
    const accounts = useOne<ProviderAccount[]>(ACCOUNTS);
    const accountCount = accounts.data?.data.filter(
        (a) => a.provider === provider,
    ).length;
    return (
        <div className="space-y-4">
            <PageHeader
                crumbs={[{ label: 'Providers', href: LIST_PAGE }]}
                title={found?.display_name ?? 'Provider'}
            />
            {canListMerchants && merchants.isError ? (
                <p role="alert" className="text-destructive text-sm">
                    The Merchant list did not load:{' '}
                    {apiErrorMessage(merchants.error)} Stored testers show their
                    merchant id.
                </p>
            ) : null}
            <DetailBody query={catalogue} title="Provider">
                {(c) => {
                    const p = c.providers.find((x) => x.code === provider);
                    if (!p)
                        return (
                            <p
                                role="alert"
                                className="text-destructive text-sm"
                            >
                                This provider is not in the catalogue.
                            </p>
                        );
                    const stages = stagesOf(c, p.code);
                    return (
                        <>
                            <Card data-testid={`provider-${p.code}`}>
                                <CardHeader>
                                    <CardTitle className="text-base">
                                        General information
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <ProviderName name={p.display_name} />
                                    <TenantProviderSetup provider={p.code} />
                                </CardContent>
                            </Card>
                            <Tabs defaultValue="accounts">
                                <TabsList>
                                    <TabsTrigger value="accounts">
                                        Accounts
                                        <TabCount count={accountCount} />
                                    </TabsTrigger>
                                    <TabsTrigger value="systems">
                                        Payment systems
                                        <TabCount count={stages.length} />
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="accounts">
                                    <ProviderAccounts provider={p} />
                                </TabsContent>
                                <TabsContent
                                    value="systems"
                                    className="space-y-8"
                                >
                                    {/* BM-110 § 9: one column on a phone, more as the width grows */}
                                    <section
                                        className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
                                        data-testid={`systems-${p.code}`}
                                    >
                                        {stages.length ? (
                                            stages.map((r) => (
                                                <StageRow
                                                    key={`${r.payment_system}-${r.version}`}
                                                    row={r}
                                                    known={known}
                                                    capabilities={
                                                        c.systems.find(
                                                            (s) =>
                                                                s.payment_system ===
                                                                r.payment_system,
                                                        )?.capabilities ?? {}
                                                    }
                                                />
                                            ))
                                        ) : (
                                            <p className="text-muted-foreground col-span-full text-sm">
                                                This provider carries no payment
                                                system yet.
                                            </p>
                                        )}
                                    </section>
                                </TabsContent>
                            </Tabs>
                        </>
                    );
                }}
            </DetailBody>
        </div>
    );
}

const ENDPOINT_SAVED = 'The endpoint is saved.';
const SWITCH_OFF_COPY =
    'No new charge, checkout session or offer goes through this provider. Webhooks and the captures, voids, refunds and renewals of existing payments continue.';

type Endpoint = Pick<TenantProvider, 'host' | 'base_url'>;
type EndpointKey = keyof Endpoint;
const ENDPOINT_FIELDS = [
    ['base_url', 'Server API'],
    ['host', 'Card form host'],
] as const satisfies readonly (readonly [EndpointKey, string])[];
/** The picker value of "no value of its own": the API takes null for it. */
const DEFAULT_ENDPOINT = 'default';

/**
 * An endpoint's choices (D-43): the default first, then each allow-listed host as the https URL the API stores. A
 * stored value that is no longer listed stays as an option, so opening the form never silently changes it. The
 * default reads as the URL it resolves to with " (default)" after it (BM-110 § 11); "…" until that URL has loaded.
 */
function endpointOptions(
    hosts: readonly string[],
    inherited: string | null,
    current: string | null,
): SelectOption[] {
    const urls = hosts.map((h) => `https://${h}`);
    if (current && !urls.includes(current)) urls.push(current);
    return [
        {
            value: DEFAULT_ENDPOINT,
            label: `${inherited ?? '…'} (default)`,
        },
        ...urls.map((u) => ({ value: u, label: u })),
    ];
}

/** The endpoint keys whose pick differs from the stored value, as the PATCH sends them. */
const changedEndpoint = (picked: Endpoint, current: Endpoint) =>
    Object.fromEntries(
        ENDPOINT_FIELDS.filter(([k]) => picked[k] !== current[k]).map(([k]) => [
            k,
            picked[k],
        ]),
    ) as Partial<Endpoint>;

type EndpointPickersProps = {
    idOf: (key: EndpointKey) => string;
    current: Endpoint;
    hosts: readonly string[];
    /** The URL each key resolves to with no value of its own; null while it loads. */
    inherited: Endpoint;
    refusal: Refusal | null;
    busy: boolean;
};

/**
 * The host and base_url pickers (BM-70 § 7b): picked from the allow-list, never typed. A refusal shows on the picker
 * its `field` names, with each failed account of a tenant default change (D-53).
 */
function EndpointPickers({
    idOf,
    current,
    hosts,
    inherited,
    refusal,
    busy,
    picked,
    onPick,
}: EndpointPickersProps & {
    picked: Endpoint;
    onPick: (picked: Endpoint) => void;
}) {
    const refusedKey = endpointKeyOf(refusal?.field ?? null);
    return (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {ENDPOINT_FIELDS.map(([k, label]) => (
                <Field key={k} id={idOf(k)} label={label}>
                    <SearchableSelect
                        id={idOf(k)}
                        label={label}
                        testId={idOf(k)}
                        options={endpointOptions(
                            hosts,
                            inherited[k],
                            current[k],
                        )}
                        value={picked[k] ?? DEFAULT_ENDPOINT}
                        clearable={false}
                        disabled={busy}
                        onChange={(v) =>
                            onPick({
                                ...picked,
                                [k]: !v || v === DEFAULT_ENDPOINT ? null : v,
                            })
                        }
                    />
                    {refusal && refusedKey === k ? (
                        <div
                            role="alert"
                            className="text-destructive space-y-1 text-xs"
                            data-testid={`${idOf(k)}-error`}
                        >
                            <p>{refusal.message}</p>
                            {refusal.accounts.length ? (
                                <ul
                                    className="list-disc pl-4"
                                    data-testid="failed-accounts"
                                >
                                    {refusal.accounts.map((a) => (
                                        <li key={a.public_id}>
                                            {`${a.name}: ${VERIFY_WORDS[a.reason] ?? a.reason}`}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    ) : null}
                </Field>
            ))}
        </div>
    );
}

/**
 * The tenant default endpoint form: the pickers and Save endpoint, which sends only the keys that changed; the API
 * verifies a moved base_url with NMI first and saves at once (D-51). On a refusal the picks stay, nothing is saved.
 */
function EndpointForm({
    onSave,
    ...pickers
}: EndpointPickersProps & {
    onSave: (changed: Partial<Endpoint>) => void;
}) {
    const [picked, setPicked] = useState<Endpoint>(pickers.current);
    const changed = changedEndpoint(picked, pickers.current);
    const dirty = Object.keys(changed).length > 0;
    return (
        <form
            method="post"
            className="space-y-3"
            onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (dirty) onSave(changed);
            }}
        >
            <EndpointPickers {...pickers} picked={picked} onPick={setPicked} />
            <Button type="submit" disabled={pickers.busy || !dirty}>
                Save endpoint
            </Button>
        </form>
    );
}

/**
 * The tenant's own setup of a provider (BM-70 § 7b, D-43..D-45, D-51): its on/off switch and its default endpoint,
 * each applied at once. Switching off is confirmed first; a moved default base_url is verified on every account it
 * moves before anything is saved, and a refusal names each failed account (D-53).
 */
function TenantProviderSetup({ provider }: { provider: string }) {
    const path = tenantProviderPath(provider);
    const query = useOne<TenantProvider>(path);
    const [confirmOff, setConfirmOff] = useState(false);
    const [note, setNote] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [refusal, setRefusal] = useState<Refusal | null>(null);
    const write = useWrite(
        async (body: TenantProviderUpdateRequest) =>
            (await api().patch<TenantProvider>(path, body)).data,
        [path, BASE],
    );
    const send = (body: TenantProviderUpdateRequest) => {
        setError(null);
        setNote(null);
        setRefusal(null);
        write.mutate(body, {
            onSuccess: () => {
                setConfirmOff(false);
                setNote(
                    body.enabled === false
                        ? 'The provider is switched off.'
                        : body.enabled === true
                          ? 'The provider is switched on.'
                          : ENDPOINT_SAVED,
                );
            },
            onError: (e) => {
                const r = refusalOf(e);
                // an endpoint refusal shows on its picker; any other on the card (or in the switch-off dialog)
                if (body.enabled === undefined && endpointKeyOf(r.field))
                    setRefusal(r);
                else setError(r.message);
            },
        });
    };
    const switchId = `tp-enabled-${provider}`;
    return (
        <section
            className="space-y-3"
            data-testid={`tenant-provider-${provider}`}
        >
            <DetailBody query={query} title="Provider setup">
                {(tp) => (
                    <>
                        <div className="flex flex-wrap items-center gap-2">
                            <Switch
                                id={switchId}
                                checked={tp.enabled}
                                disabled={write.isPending}
                                onCheckedChange={(on) =>
                                    on
                                        ? send({ enabled: true })
                                        : setConfirmOff(true)
                                }
                            />
                            <label htmlFor={switchId} className="text-sm">
                                Provider on
                            </label>
                        </div>
                        <EndpointForm
                            // a saved change reloads the pickers from the stored endpoint
                            key={`${tp.base_url}|${tp.host}`}
                            idOf={(k) => `tp-${k}-${provider}`}
                            current={{ host: tp.host, base_url: tp.base_url }}
                            hosts={tp.allowed_hosts}
                            // the tenant row's own default falls back to the built-in URLs (bm-110 § 18)
                            inherited={{
                                host: tp.default_host,
                                base_url: tp.default_base_url,
                            }}
                            refusal={refusal}
                            busy={write.isPending}
                            onSave={send}
                        />
                    </>
                )}
            </DetailBody>
            {note ? (
                <p role="status" className="text-sm">
                    {note}
                </p>
            ) : null}
            {error && !confirmOff ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
            <ConfirmDialog
                open={confirmOff}
                onOpenChange={(o) => {
                    if (!o) {
                        setConfirmOff(false);
                        setError(null);
                    }
                }}
                title="Switch the provider off?"
                description={SWITCH_OFF_COPY}
                confirmLabel="Switch off"
                destructive
                busy={write.isPending}
                onConfirm={() => send({ enabled: false })}
            >
                {error ? (
                    <p role="alert" className="text-destructive text-xs">
                        {error}
                    </p>
                ) : null}
            </ConfirmDialog>
        </section>
    );
}

type ProviderField = Provider['fields'][number];
type KeyChanges = NonNullable<ProviderAccountUpdateRequest['credentials']>;
type PlainValues = Record<string, string | boolean>;
/** A declared plain field, with the help text admin shows under its input (bm-110 § 7). */
type PlainField = ProviderField & { hint?: string };

/**
 * bm-110 § 7 (fold-rev-8, Q-14): NMI's Gateway ID is required in Add account and the edit form, and says what it is.
 * ponytail: keyed by `<provider code>.<field>` — NMI's catalogue code is `gw_01` (api PaymentSystemCatalog::PROVIDER_GW_01),
 * never the brand — because the catalogue declares gateway_id optional and carries no help text; upgrade: declare
 * `required` and a help text in the api's ProviderFields, then drop this map.
 */
const PLAIN_FIELD_RULES: Record<string, Partial<PlainField>> = {
    'gw_01.gateway_id': {
        required: true,
        hint: 'The merchant id NMI sends in webhooks.',
    },
};

/** The provider's declared plain fields (kind plain), in their declared order, with `PLAIN_FIELD_RULES` applied. */
const plainFieldsOf = (provider: Provider | undefined): PlainField[] =>
    (provider?.fields ?? [])
        .filter((f) => f.kind === 'plain')
        .map((f) => ({
            ...f,
            ...PLAIN_FIELD_RULES[`${provider?.code}.${f.key}`],
        }));

/** The required text fields left empty: each blocks the submit with its own error. */
const emptyRequired = (fields: readonly PlainField[], values: PlainValues) =>
    fields
        .filter(
            (f) =>
                f.required &&
                f.type !== 'boolean' &&
                !String(values[f.key] ?? '').trim(),
        )
        .map((f) => f.key);

/** A refusal's message on the plain text field it names, keyed by that field. */
type PlainErrors = Readonly<Record<string, string>>;

/**
 * The plain text field a refused save names in `field` (`config.<key>`, bm-110 § 7), with its message: admin's own
 * `Enter the …` when that required field was left empty, else the api's words. Null when `field` names none of
 * `fields` (a bare `config`, an endpoint key), so the refusal stays where it was shown before.
 */
function plainErrorsOf(
    r: Refusal,
    fields: readonly PlainField[],
    values: PlainValues,
): PlainErrors | null {
    const key = r.field?.startsWith('config.')
        ? r.field.slice('config.'.length)
        : null;
    const f = fields.find((d) => d.key === key && d.type !== 'boolean');
    if (!f) return null;
    const empty = f.required && !String(values[f.key] ?? '').trim();
    return {
        [f.key]: empty ? `Enter the ${f.label.toLowerCase()}.` : r.message,
    };
}

type PlainFieldsProps = {
    id: string;
    /** The provider's plain fields, from `plainFieldsOf`. */
    fields: readonly PlainField[];
    values: PlainValues;
    onChange: (key: string, value: string | boolean) => void;
    /** The required fields a submit found empty, each shown with its own error. */
    missing?: readonly string[];
    /** A refused save's message on the field it names (`plainErrorsOf`). */
    errors?: PlainErrors | null;
};

/** A provider's plain text fields as inputs (bm-110 Q-2): the switches go last, through `PlainSwitches`. */
function PlainInputs({
    id,
    fields,
    values,
    onChange,
    missing = [],
    errors = null,
}: PlainFieldsProps) {
    return fields
        .filter((f) => f.type !== 'boolean')
        .map((f) => {
            const error =
                missing.includes(f.key) && !String(values[f.key] ?? '').trim()
                    ? `Enter the ${f.label.toLowerCase()}.`
                    : errors?.[f.key];
            return (
                <Field
                    key={f.key}
                    id={`${id}-${f.key}`}
                    label={f.required ? f.label : `${f.label} (optional)`}
                    hint={f.hint}
                    error={error}
                >
                    <Input
                        id={`${id}-${f.key}`}
                        value={String(values[f.key] ?? '')}
                        aria-invalid={!!error}
                        onChange={(e) => onChange(f.key, e.target.value)}
                    />
                </Field>
            );
        });
}

/** A provider's plain boolean fields (bm-110 Q-2): last, in their own group behind a separator; nothing when none. */
function PlainSwitches({ id, fields, values, onChange }: PlainFieldsProps) {
    const switches = fields.filter((f) => f.type === 'boolean');
    if (!switches.length) return null;
    return (
        <>
            <Separator />
            <div className="space-y-3" data-testid={`${id}-switches`}>
                {switches.map((f) => (
                    <div key={f.key} className="flex items-center gap-2">
                        <Switch
                            id={`${id}-${f.key}`}
                            checked={values[f.key] === true}
                            onCheckedChange={(on) => onChange(f.key, on)}
                        />
                        <label htmlFor={`${id}-${f.key}`} className="text-sm">
                            {f.label}
                        </label>
                    </div>
                ))}
            </div>
        </>
    );
}

/** An account's status badge: verified, unverified or deactivated. */
function AccountBadge({ account }: { account: ProviderAccount }) {
    return (
        <Badge
            variant={
                account.status === 'verified'
                    ? 'success'
                    : account.status === 'disabled'
                      ? 'neutral'
                      : 'warning'
            }
        >
            {codeLabel(account.status)}
        </Badge>
    );
}

/** BM-110 Q-1: the Accounts table's mark on the provider's default account; a focusable span so the tooltip opens by keyboard too. */
function DefaultAccountBadge() {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    tabIndex={0}
                    className="inline-flex"
                    data-testid="account-default-badge"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Badge variant="neutral" size="sm">
                        Default
                        <InfoIcon aria-hidden className="size-3" />
                    </Badge>
                </span>
            </TooltipTrigger>
            <TooltipContent>
                Merchants approved in this tenant are linked to this account.
            </TooltipContent>
        </Tooltip>
    );
}

/** An account's status label: its badge, with when. */
function AccountStatus({ account }: { account: ProviderAccount }) {
    return (
        <span className="flex flex-wrap items-center gap-2">
            <AccountBadge account={account} />
            <span className="text-muted-foreground text-xs">
                {account.status === 'disabled' ? (
                    <>
                        Deactivated{' '}
                        <DateTime value={account.disabled_at} empty="—" />
                    </>
                ) : account.verified_at ? (
                    <>
                        Last verified{' '}
                        <DateTime value={account.verified_at} empty="—" />
                    </>
                ) : (
                    'Never verified'
                )}
            </span>
        </span>
    );
}

/**
 * A provider's Accounts tab (BM-70 § 7b, QA-23): each account of the tenant pool by name with its status,
 * Merchant count; a row opens the account's detail. Then Add account. No tenant-default column (D-49, T-90); the
 * provider's default account carries a 'Default' badge beside its name (bm-110 Q-1, QA-1).
 */
function ProviderAccounts({ provider }: { provider: Provider }) {
    const accounts = useOne<ProviderAccount[]>(ACCOUNTS);
    const usages = useOne<ProviderAccountUsage[]>(BINDINGS);
    const navigate = useNavigate();
    const mine = (accounts.data?.data ?? []).filter(
        (a) => a.provider === provider.code,
    );
    const live = usages.data?.data;
    return (
        <section
            className="space-y-3"
            data-testid={`accounts-${provider.code}`}
        >
            {accounts.isError ? (
                <p role="alert" className="text-destructive text-sm">
                    The accounts did not load: {apiErrorMessage(accounts.error)}
                </p>
            ) : null}
            {usages.isError ? (
                <p role="alert" className="text-destructive text-sm">
                    Who uses each account did not load:{' '}
                    {apiErrorMessage(usages.error)}
                </p>
            ) : null}
            <ListTable<ProviderAccount>
                rows={mine}
                rowKey={(a) => a.id}
                loading={accounts.isPending}
                rowProps={(a) => ({
                    onClick: () => navigate(accountPage(provider.code, a.id)),
                    className: 'cursor-pointer',
                })}
                emptyState={
                    <p className="text-muted-foreground p-6 text-center text-sm">
                        No account yet.
                    </p>
                }
                columns={[
                    {
                        key: 'name',
                        label: 'Name',
                        cell: (a) => (
                            <span className="inline-flex flex-wrap items-center gap-2">
                                <Link
                                    className="font-medium break-all underline-offset-4 hover:underline"
                                    to={accountPage(provider.code, a.id)}
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`account-${a.id}`}
                                >
                                    {a.name}
                                </Link>
                                {a.default ? <DefaultAccountBadge /> : null}
                            </span>
                        ),
                    },
                    {
                        key: 'status',
                        label: 'Status',
                        cell: (a) => <AccountStatus account={a} />,
                    },
                    {
                        key: 'merchants',
                        label: 'Merchants',
                        cell: (a) => (
                            <span data-testid={`account-merchants-${a.id}`}>
                                {live
                                    ? String(merchantsOf(live, a.id).length)
                                    : '…'}
                            </span>
                        ),
                    },
                ]}
            />
            <AddAccount provider={provider} />
        </section>
    );
}

/**
 * Add account (BM-70 § 7, § 7b, D-51): the name, the provider's declared plain fields and its
 * declared secret fields, POSTed once; the API verifies the keys with NMI and saves the account at once (201), or
 * refuses with nothing saved (422, shown on the keys). No field key is written here: the form renders from the
 * catalogue's declaration (D-47).
 */
function AddAccount({ provider }: { provider: Provider }) {
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [plain, setPlain] = useState<Record<string, string | boolean>>({});
    const [missing, setMissing] = useState(false);
    const [missingPlain, setMissingPlain] = useState<readonly string[]>([]);
    const [note, setNote] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    const [plainErrors, setPlainErrors] = useState<PlainErrors | null>(null);
    const create = useWrite(
        async (body: ProviderAccountRequest) =>
            (await api().post<ProviderAccount>(ACCOUNTS, body)).data,
        [ACCOUNTS, BASE],
    );
    const id = `add-account-${provider.code}`;
    const close = () => {
        setOpen(false);
        setError(null);
        setRefused(null);
        setPlainErrors(null);
        setMissing(false);
        setMissingPlain([]);
        setName('');
        setPlain({});
    };
    if (!open)
        return (
            <div className="space-y-2">
                {note ? (
                    <p role="status" className="text-sm">
                        {note}
                    </p>
                ) : null}
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                        setNote(null);
                        setOpen(true);
                    }}
                >
                    Add account
                </Button>
            </div>
        );
    const config = Object.fromEntries(
        Object.entries(plain)
            .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
            .filter(([, v]) => v !== '' && v !== false),
    );
    // bm-110 Q-2: the text inputs first; the declared switches last, in their own group below the keys
    const declared = plainFieldsOf(provider);
    const setField = (key: string, value: string | boolean) => {
        setPlain((p) => ({ ...p, [key]: value }));
        setPlainErrors((e) => (e?.[key] ? null : e));
    };
    return (
        <div className="space-y-3" data-testid={id}>
            <CredentialsForm
                id={id}
                fields={provider.fields.filter((f) => f.kind === 'secret')}
                submitLabel="Save account"
                hint="The keys are checked with the provider first and the account is saved at once; a key is never shown again."
                refused={refused}
                focusOn={plainErrors}
                busy={create.isPending}
                after={
                    <PlainSwitches
                        id={id}
                        fields={declared}
                        values={plain}
                        onChange={setField}
                    />
                }
                onCancel={close}
                onSubmit={(credentials) => {
                    setError(null);
                    setRefused(null);
                    setPlainErrors(null);
                    const ok = !!name.trim();
                    const empty = emptyRequired(declared, plain);
                    setMissing(!ok);
                    setMissingPlain(empty);
                    if (!ok || empty.length) return;
                    create.mutate(
                        {
                            name: name.trim(),
                            provider: provider.code,
                            credentials,
                            ...(Object.keys(config).length ? { config } : {}),
                        },
                        {
                            onSuccess: (a) => {
                                close();
                                setNote(verifiedNote(a));
                            },
                            onError: (e) => {
                                const r = refusalOf(e);
                                // a refusal naming a plain field sits on that field; one naming no input stays a line
                                const onField = plainErrorsOf(
                                    r,
                                    declared,
                                    plain,
                                );
                                if (r.field === 'credentials')
                                    setRefused(r.message);
                                else if (onField) setPlainErrors(onField);
                                else setError(r.message);
                            },
                        },
                    );
                }}
            >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Field
                        id={`${id}-name`}
                        label="Name"
                        error={
                            missing && !name.trim()
                                ? 'Enter the account name.'
                                : undefined
                        }
                    >
                        <Input
                            id={`${id}-name`}
                            value={name}
                            maxLength={100}
                            aria-invalid={missing && !name.trim()}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Field>
                    <PlainInputs
                        id={id}
                        fields={declared}
                        values={plain}
                        onChange={setField}
                        missing={missingPlain}
                        errors={plainErrors}
                    />
                </div>
            </CredentialsForm>
            {error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
        </div>
    );
}

/**
 * The write-only credential set (BM-70 § 7): one password input per declared secret field, empty ones left out (the
 * API refuses an empty string), every required one needed. With `keep` (the account card's edit, bm-110 § 7) no field
 * is required and a field left empty keeps its stored value (the API merges, D-21). `children` render above the keys,
 * inside the same form; `after` renders below the keys, above the buttons (bm-110 Q-2: Add account's switches). The parent unmounts the form on success, so no secret stays in state; `refused` is the
 * provider's refusal of the keys (the 422 naming `credentials`), shown on the keys while the form stays as typed.
 * After a submit, and whenever `focusOn` turns non-null (a refusal set on a field), the first invalid input is focused.
 */
function CredentialsForm({
    id,
    fields,
    keep = false,
    submitLabel,
    hint,
    refused = null,
    focusOn = null,
    busy,
    onSubmit,
    onCancel,
    children,
    after,
}: {
    id: string;
    fields: readonly ProviderField[];
    keep?: boolean;
    submitLabel: string;
    hint: string;
    refused?: string | null;
    focusOn?: unknown;
    busy: boolean;
    onSubmit: (credentials: KeyChanges) => void;
    onCancel: () => void;
    children?: ReactNode;
    after?: ReactNode;
}) {
    const [values, setValues] = useState<Record<string, string>>({});
    const [missing, setMissing] = useState<readonly string[]>([]);
    const [submits, setSubmits] = useState(0);
    const form = useRef<HTMLFormElement>(null);
    // bm-110 § 7: an error highlights its input (aria-invalid) and the first one takes the focus
    const focusInvalid = () =>
        form.current
            ?.querySelector<HTMLElement>('[aria-invalid="true"]')
            ?.focus();
    useEffect(() => {
        if (submits) focusInvalid();
    }, [submits]);
    useEffect(() => {
        if (focusOn != null) focusInvalid();
    }, [focusOn]);
    return (
        <form
            ref={form}
            method="post"
            className="space-y-3"
            autoComplete="off"
            onSubmit={(e: FormEvent) => {
                e.preventDefault();
                setSubmits((n) => n + 1);
                const set: Record<string, string> = Object.fromEntries(
                    fields
                        .map((f) => [f.key, values[f.key]?.trim() ?? ''])
                        .filter(([, v]) => v),
                );
                const absent = keep
                    ? []
                    : fields
                          .filter((f) => f.required && !set[f.key])
                          .map((f) => f.key);
                setMissing(absent);
                // the parent checks its own inputs too, so it is told even when a key is missing
                if (!absent.length) onSubmit(set);
            }}
        >
            {children}
            <p className="text-muted-foreground text-xs">{hint}</p>
            {refused ? (
                <p
                    role="alert"
                    className="text-destructive text-xs"
                    data-testid={`${id}-refused`}
                >
                    {refused}
                </p>
            ) : null}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {fields.map((f) => (
                    <Field
                        key={f.key}
                        id={`${id}-${f.key}`}
                        label={
                            keep
                                ? `${f.label} (leave empty to keep)`
                                : f.required
                                  ? f.label
                                  : `${f.label} (optional)`
                        }
                        error={
                            !keep && missing.includes(f.key)
                                ? `Enter the ${f.label.toLowerCase()}.`
                                : undefined
                        }
                    >
                        <Input
                            id={`${id}-${f.key}`}
                            type="password"
                            autoComplete="new-password"
                            aria-invalid={!keep && missing.includes(f.key)}
                            value={values[f.key] ?? ''}
                            onChange={(e) =>
                                setValues((v) => ({
                                    ...v,
                                    [f.key]: e.target.value,
                                }))
                            }
                        />
                    </Field>
                ))}
            </div>
            {after}
            <div className="flex gap-2">
                <Button type="submit" disabled={busy}>
                    {submitLabel}
                </Button>
                <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        </form>
    );
}

/** What a verify answered, as staff read it: the provider confirmed the account's credentials, or not. */
const verifiedNote = (a: ProviderAccount) =>
    a.status === 'verified'
        ? `Account ${a.name} is verified.`
        : a.status === 'disabled'
          ? `Account ${a.name} is deactivated.`
          : `Account ${a.name} is not verified: the provider did not confirm these credentials.`;

/** The account's lifecycle action (BM-70 § 7b): Deactivate at once; Reactivate after the NMI verify (D-54, T-96). */
const LIFECYCLE = {
    deactivate: {
        action: 'Deactivate',
        hint: 'A deactivated account cannot be assigned until it is reactivated.',
        confirm:
            'A Merchant using it has no verified account until it is reactivated or staff link another. Reactivate checks the stored keys with NMI first.',
    },
    reactivate: {
        action: 'Reactivate',
        hint: 'Reactivate checks the stored keys and endpoint with NMI first; the account is used again only once NMI verifies it.',
        confirm:
            'NMI checks the stored keys and endpoint first; nothing changes if it refuses or cannot be reached.',
    },
} as const;

function Outcome({
    note,
    error,
}: {
    note: string | null;
    error: string | null;
}) {
    return (
        <>
            {note ? (
                <p role="status" className="text-sm">
                    {note}
                </p>
            ) : null}
            {error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
        </>
    );
}

/**
 * An account's detail (BM-70 § 7b, bm-110 § 7): one card, read view by default, edited in place. Assigning Merchants
 * stays on the merchant page (bm-110 REQ-14); a refused save shows on the input it names (D-51).
 */
export function ProviderAccountPage() {
    const { provider = '', account = '' } = useParams();
    const catalogue = useOne<Catalogue>(BASE);
    const accounts = useOne<ProviderAccount[]>(ACCOUNTS);
    const setup = useOne<TenantProvider>(tenantProviderPath(provider)).data
        ?.data;
    const p = catalogue.data?.data.providers.find((x) => x.code === provider);
    const found = accounts.data?.data.find(
        (a) => String(a.id) === account && a.provider === provider,
    );
    return (
        <div className="space-y-4">
            <PageHeader
                crumbs={[
                    { label: 'Providers', href: LIST_PAGE },
                    {
                        label: p?.display_name ?? 'Provider',
                        href: providerPage(provider),
                    },
                ]}
                title={found?.name ?? 'Account'}
                status={found ? <AccountStatus account={found} /> : undefined}
            />
            <DetailBody query={accounts} title="Account">
                {() =>
                    found ? (
                        <AccountDetail
                            key={found.id}
                            account={found}
                            provider={p}
                            hosts={setup?.allowed_hosts ?? []}
                            // T-16: what the tenant row resolves to, its own URL else the built-in one (bm-110 § 18)
                            inherited={{
                                host:
                                    setup?.host ?? setup?.default_host ?? null,
                                base_url:
                                    setup?.base_url ??
                                    setup?.default_base_url ??
                                    null,
                            }}
                        />
                    ) : (
                        <p role="alert" className="text-destructive text-sm">
                            This account is not in the tenant&apos;s pool.
                        </p>
                    )
                }
            </DetailBody>
        </div>
    );
}

/** A plain config field on the read view (bm-110 § 7): On / Off for a flag, the text as stored, — when it has no value. */
const plainValue = (v: string | boolean | null | undefined) =>
    typeof v === 'boolean' ? (v ? 'On' : 'Off') : v || '—';

/** Why the default switch is off (bm-110 § 7, the default_account_unusable rule of § 6). */
const DEFAULT_REASON = 'The default needs a verified, active account.';

/**
 * The account as one card (bm-110 § 7): a read view of every field (name, provider, each endpoint key as its URL then
 * " (tenant default)" when inherited, each key's masked hint, each declared plain config field read-only, status, last
 * verified) with the default switch, Verify and
 * Deactivate, or Reactivate after the NMI verify (D-54). The edit icon at the top right opens name, the plain fields
 * prefilled (switches last, bm-110 QA-9), endpoint and keys in the same card; the changed plain fields and endpoint
 * keys travel in the one config PATCH. Save sends one PATCH per changed field (name, config, credentials) and stops at the first refusal,
 * shown on the input it names (T-24). A blank key keeps the stored key.
 */
function AccountDetail({
    account,
    provider,
    hosts,
    inherited,
}: {
    account: ProviderAccount;
    provider: Provider | undefined;
    hosts: readonly string[];
    /** The URL each key resolves to with no override: the tenant provider's own, else its built-in one (T-16). */
    inherited: Endpoint;
}) {
    const current: Endpoint = {
        host: account.config.host ?? null,
        base_url: account.config.base_url ?? null,
    };
    const plain = plainFieldsOf(provider);
    // bm-110 QA-9: the edit form's plain fields start at the stored values (a switch never set reads off)
    const stored: PlainValues = Object.fromEntries(
        plain.map((f) => {
            const v = account.config[f.key];
            return [f.key, f.type === 'boolean' ? v === true : String(v ?? '')];
        }),
    );
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(account.name);
    const [picked, setPicked] = useState<Endpoint>(current);
    const [values, setValues] = useState<PlainValues>(stored);
    const [missingName, setMissingName] = useState(false);
    const [missingPlain, setMissingPlain] = useState<readonly string[]>([]);
    const [saving, setSaving] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [note, setNote] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    // the 422 of a verify-first save, kept on the open form whose input it names
    const [refusal, setRefusal] = useState<Refusal | null>(null);
    const [plainErrors, setPlainErrors] = useState<PlainErrors | null>(null);
    const path = `${ACCOUNTS}/${account.id}`;
    const invalidate = [ACCOUNTS, BASE];
    const verify = useWrite(
        async () => (await api().post<ProviderAccount>(`${path}/verify`)).data,
        invalidate,
    );
    const update = useWrite(
        async (body: ProviderAccountUpdateRequest) =>
            (await api().patch<ProviderAccount>(path, body)).data,
        invalidate,
    );
    const reset = () => {
        setNote(null);
        setError(null);
        setRefusal(null);
        setPlainErrors(null);
    };
    // Verify, the default switch and the lifecycle: one request each; `done` words the success, else what the verify answered
    const send = (body: ProviderAccountUpdateRequest | null, done?: string) => {
        reset();
        const handlers = {
            onSuccess: (a: ProviderAccount) => {
                setNote(done ?? verifiedNote(a));
                setConfirm(false);
            },
            onError: (e: unknown) => setError(refusalOf(e).message),
        };
        if (body === null) verify.mutate(undefined, handlers);
        else update.mutate(body, handlers);
    };
    const edit = () => {
        reset();
        setName(account.name);
        setPicked(current);
        setValues(stored);
        setMissingName(false);
        setMissingPlain([]);
        setEditing(true);
    };
    // one PATCH per changed field, in the order name, config, credentials; the first refusal stops the rest (T-24)
    const save = async (credentials: KeyChanges) => {
        const trimmed = name.trim();
        const empty = emptyRequired(plain, values);
        setMissingName(!trimmed);
        setMissingPlain(empty);
        if (!trimmed || empty.length) return;
        // the endpoint keys and the plain fields that changed travel in the one config PATCH (bm-110 QA-9)
        // ponytail: cast — the generated intersection types the plain-field index (string | boolean) over host / base_url
        // too, so a cleared override (null) does not type-check though the contract allows it; drop when the generator
        // emits additionalProperties as a union with the declared properties.
        const config = {
            ...changedEndpoint(picked, current),
            ...Object.fromEntries(
                Object.entries(values).flatMap(([k, v]) => {
                    const sent = typeof v === 'string' ? v.trim() : v;
                    return sent === stored[k] ? [] : [[k, sent]];
                }),
            ),
        } as NonNullable<ProviderAccountUpdateRequest['config']>;
        const bodies: ProviderAccountUpdateRequest[] = [
            ...(trimmed !== account.name ? [{ name: trimmed }] : []),
            ...(Object.keys(config).length ? [{ config }] : []),
            ...(Object.keys(credentials).length ? [{ credentials }] : []),
        ];
        reset();
        setSaving(true);
        let saved: ProviderAccount | null = null;
        for (const body of bodies) {
            try {
                saved = await update.mutateAsync(body);
            } catch (e) {
                const r = refusalOf(e);
                const onField = body.config
                    ? plainErrorsOf(r, plain, values)
                    : null;
                // keys, endpoint and plain-field refusals stay on their input (§ 7b); one naming no input on the card
                if (onField) setPlainErrors(onField);
                else if (
                    (body.credentials && r.field === 'credentials') ||
                    (body.config && endpointKeyOf(r.field))
                )
                    setRefusal(r);
                else setError(r.message);
                setSaving(false);
                return;
            }
        }
        setSaving(false);
        setEditing(false);
        if (saved)
            setNote(
                Object.keys(credentials).length
                    ? verifiedNote(saved)
                    : 'The account is saved.',
            );
    };
    const off = account.status === 'disabled';
    const lifecycle = off ? LIFECYCLE.reactivate : LIFECYCLE.deactivate;
    const busy = saving || verify.isPending || update.isPending;
    const secret = (provider?.fields ?? []).filter((f) => f.kind === 'secret');
    const setField = (key: string, value: string | boolean) => {
        setValues((v) => ({ ...v, [key]: value }));
        setPlainErrors((e) => (e?.[key] ? null : e));
    };
    const formId = `update-keys-${account.id}`;
    const usable = account.status === 'verified';
    const defaultId = `account-default-${account.id}`;
    const endpointLine = (k: EndpointKey) =>
        current[k] ?? `${inherited[k] ?? '…'} (tenant default)`;
    return (
        <Card data-testid="account-card">
            <CardHeader className="flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">Account</CardTitle>
                {off || editing ? null : (
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label="Edit account"
                        disabled={busy}
                        onClick={edit}
                    >
                        <EditIcon className="size-4" aria-hidden />
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {editing ? (
                    <CredentialsForm
                        id={formId}
                        fields={secret}
                        keep
                        submitLabel="Save"
                        hint="Leave a key empty to keep the stored one. Changed keys are checked with the provider first, then saved at once."
                        refused={
                            refusal?.field === 'credentials'
                                ? refusal.message
                                : null
                        }
                        focusOn={plainErrors}
                        busy={busy}
                        after={
                            <PlainSwitches
                                id={formId}
                                fields={plain}
                                values={values}
                                onChange={setField}
                            />
                        }
                        onCancel={() => {
                            reset();
                            setEditing(false);
                        }}
                        onSubmit={(credentials) => void save(credentials)}
                    >
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <Field
                                id={`account-name-${account.id}`}
                                label="Name"
                                error={
                                    missingName && !name.trim()
                                        ? 'Enter the account name.'
                                        : undefined
                                }
                            >
                                <Input
                                    id={`account-name-${account.id}`}
                                    value={name}
                                    maxLength={100}
                                    aria-invalid={missingName && !name.trim()}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </Field>
                            <PlainInputs
                                id={formId}
                                fields={plain}
                                values={values}
                                onChange={setField}
                                missing={missingPlain}
                                errors={plainErrors}
                            />
                        </div>
                        <EndpointPickers
                            idOf={(k) => `account-${k}-${account.id}`}
                            current={current}
                            hosts={hosts}
                            inherited={inherited}
                            refusal={refusal}
                            busy={busy}
                            picked={picked}
                            onPick={setPicked}
                        />
                    </CredentialsForm>
                ) : (
                    <>
                        <KeyValueList
                            items={[
                                { label: 'Name', value: account.name },
                                {
                                    label: 'Provider',
                                    value: provider?.display_name ?? '…',
                                },
                                ...ENDPOINT_FIELDS.map(([k, label]) => ({
                                    key: k,
                                    label,
                                    value: endpointLine(k),
                                })),
                                ...secret.map((f) => ({
                                    key: f.key,
                                    label: f.label,
                                    value: account.key_hints[f.key] ?? '—',
                                    mono: true,
                                })),
                                ...plain.map((f) => ({
                                    key: f.key,
                                    label: f.label,
                                    value: plainValue(account.config[f.key]),
                                })),
                                {
                                    label: 'Status',
                                    value: off ? (
                                        <AccountStatus account={account} />
                                    ) : (
                                        <AccountBadge account={account} />
                                    ),
                                },
                                {
                                    label: 'Last verified',
                                    value: (
                                        <DateTime
                                            zone="inline"
                                            value={account.verified_at}
                                            empty="Never verified"
                                        />
                                    ),
                                },
                                {
                                    label: 'Default',
                                    value: (
                                        <span className="flex flex-col gap-1">
                                            <span className="flex items-center gap-2">
                                                <Switch
                                                    id={defaultId}
                                                    checked={
                                                        account.default ?? false
                                                    }
                                                    disabled={!usable || busy}
                                                    onCheckedChange={(on) =>
                                                        send(
                                                            { default: on },
                                                            on
                                                                ? 'The account is the default.'
                                                                : 'The account is no longer the default.',
                                                        )
                                                    }
                                                />
                                                <label
                                                    htmlFor={defaultId}
                                                    className="text-sm"
                                                >
                                                    Default account
                                                </label>
                                            </span>
                                            {usable ? null : (
                                                <span className="text-muted-foreground text-xs">
                                                    {DEFAULT_REASON}
                                                </span>
                                            )}
                                        </span>
                                    ),
                                },
                            ]}
                        />
                        <div className="flex flex-wrap items-start gap-4">
                            {off ? null : (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={busy}
                                    onClick={() => send(null)}
                                >
                                    Verify
                                </Button>
                            )}
                            <div
                                className="space-y-2"
                                data-testid={
                                    off
                                        ? 'account-reactivate'
                                        : 'account-deactivate'
                                }
                            >
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={off ? 'default' : 'destructive'}
                                    disabled={busy}
                                    onClick={() => {
                                        setError(null);
                                        setConfirm(true);
                                    }}
                                >
                                    {lifecycle.action}
                                </Button>
                                <p className="text-muted-foreground text-xs">
                                    {lifecycle.hint}
                                </p>
                            </div>
                        </div>
                    </>
                )}
                <Outcome note={note} error={confirm ? null : error} />
            </CardContent>
            <ConfirmDialog
                open={confirm}
                onOpenChange={(o) => {
                    if (!o) {
                        setConfirm(false);
                        setError(null);
                    }
                }}
                title={`${lifecycle.action} account ${account.name}?`}
                description={lifecycle.confirm}
                confirmLabel={lifecycle.action}
                destructive={!off}
                busy={update.isPending}
                onConfirm={() => send({ disabled: !off })}
            >
                {error ? (
                    <p role="alert" className="text-destructive text-xs">
                        {error}
                    </p>
                ) : null}
            </ConfirmDialog>
        </Card>
    );
}

/** BM-110 D-1, QA-17: an info tooltip beside a field label; a focusable span, not a button, since it sits inside the field's label. */
function LabelInfo({ label, children }: { label: string; children: string }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    role="img"
                    aria-label={label}
                    tabIndex={0}
                    className="text-muted-foreground inline-flex"
                >
                    <InfoIcon aria-hidden className="size-3.5" />
                </span>
            </TooltipTrigger>
            <TooltipContent>{children}</TooltipContent>
        </Tooltip>
    );
}

function TestMerchantsInfo() {
    return (
        <LabelInfo label="About test merchants">
            Merchants that see this payment system while it is in testing.
        </LabelInfo>
    );
}

/** One payment system the provider carries, as a card of the BM-110 § 9 grid: its icon, name and capabilities, the stage select and the tester list. Keyed by version, so a saved row resets it. */
function StageRow({
    row,
    known,
    capabilities,
}: {
    row: PaymentSystemStage;
    known: SelectOption[] | null;
    capabilities: Record<string, boolean>;
}) {
    const path = `${BASE}/${row.payment_system}/providers/${row.provider}`;
    const id = `${row.payment_system}-${row.provider}`;
    const [confirm, setConfirm] = useState<Stage | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [testers, setTesters] = useState<string[]>([
        ...row.tester_merchant_ids,
    ]);
    const [testersText, setTestersText] = useState(
        row.tester_merchant_ids.join(', '),
    );
    const setStage = useWrite(
        (stage: Stage) => api().put(`${path}/stage`, { stage }),
        [BASE],
    );
    const saveTesters = useWrite(
        (merchant_ids: string[]) =>
            api().put(`${path}/testers`, { merchant_ids }),
        [BASE],
    );
    const write = (stage: Stage) => {
        setError(null);
        setStage.mutate(stage, {
            onSuccess: () => setConfirm(null),
            onError: (e) => setError(apiErrorMessage(e)),
        });
    };
    const search = useMerchantSearch();
    // A stored tester neither on the loaded page of Merchants nor found by a search shows as its raw id.
    const testerLabel = (t: string) =>
        search.found.get(t) ?? known?.find((o) => o.value === t)?.label ?? t;
    return (
        <Card className="gap-4" data-testid={`stage-row-${row.payment_system}`}>
            <CardHeader>
                <h4 className="flex items-center gap-2 text-sm font-semibold">
                    <PaymentSystemIcon system={row.payment_system} />
                    {paymentSystemLabel(row.payment_system)}
                </h4>
                <p className="text-muted-foreground text-xs">
                    {Object.entries(capabilities)
                        .filter(([, on]) => on)
                        .map(([k]) => codeLabel(k))
                        .join(' · ')}
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                <Field id={`stage-${id}`} label="Stage">
                    <SearchableSelect
                        id={`stage-${id}`}
                        label="Stage"
                        testId={`stage-${id}`}
                        options={optionsOf(STAGES)}
                        value={row.stage}
                        clearable={false}
                        disabled={setStage.isPending}
                        onChange={(v) => {
                            const stage = v as Stage | null;
                            if (!stage || stage === row.stage) return;
                            if (CONFIRM_COPY[stage]) setConfirm(stage);
                            else write(stage);
                        }}
                    />
                </Field>
                <div className="space-y-2" data-testid={`testers-${id}`}>
                    {known ? (
                        <Field
                            id={`testers-select-${id}`}
                            label={
                                <span className="inline-flex items-center gap-1">
                                    Test merchants
                                    <TestMerchantsInfo />
                                </span>
                            }
                        >
                            <SearchableMultiSelect
                                id={`testers-select-${id}`}
                                label="Test merchant"
                                testId={`testers-select-${id}`}
                                loadOptions={search.load}
                                known={testers.map((t) => ({
                                    value: t,
                                    label: testerLabel(t),
                                }))}
                                value={testers}
                                onChange={setTesters}
                            />
                        </Field>
                    ) : (
                        <Field
                            id={`testers-text-${id}`}
                            label="Test merchant ids"
                            hint="Merchant ids separated by commas."
                        >
                            <Input
                                id={`testers-text-${id}`}
                                value={testersText}
                                onChange={(e) => setTestersText(e.target.value)}
                            />
                        </Field>
                    )}
                    {known ? (
                        <p className="text-muted-foreground text-xs">
                            {testers.length
                                ? testers.map(testerLabel).join(', ')
                                : 'No test merchants'}
                        </p>
                    ) : null}
                    <SearchError error={search.error} />
                    <Button
                        type="button"
                        variant="outline"
                        disabled={saveTesters.isPending}
                        onClick={() => {
                            setError(null);
                            saveTesters.mutate(
                                known ? testers : splitList(testersText),
                                {
                                    onError: (e) =>
                                        setError(apiErrorMessage(e)),
                                },
                            );
                        }}
                    >
                        Save test merchants
                    </Button>
                </div>
                {error && !confirm ? (
                    <p role="alert" className="text-destructive text-sm">
                        {error}
                    </p>
                ) : null}
            </CardContent>
            <ConfirmDialog
                open={confirm !== null}
                onOpenChange={(o) => {
                    if (!o) {
                        setConfirm(null);
                        setError(null);
                    }
                }}
                title={`Change stage to ${confirm ?? ''}?`}
                description={confirm ? CONFIRM_COPY[confirm] : undefined}
                confirmLabel="Change stage"
                destructive={confirm === 'disabled'}
                busy={setStage.isPending}
                onConfirm={() => (confirm ? write(confirm) : undefined)}
            >
                {error ? (
                    <p role="alert" className="text-destructive text-xs">
                        {error}
                    </p>
                ) : null}
            </ConfirmDialog>
        </Card>
    );
}

/**
 * A provider's staff-only display name, read-only (BM-70 § 7, § 10). Its rename (PATCH /staff/providers/{code}, the
 * platform-only payments.providers.manage) is answered on the platform host alone (BM-105), and this page renders on
 * tenant boards only, so it offers no edit; a platform-host provider page is a later ticket (rocnzon, 2026-10-23).
 * The code is the immutable key and is never shown.
 */
function ProviderName({ name }: { name: string }) {
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Display name:</span>
            <span className="font-medium">{name}</span>
        </div>
    );
}

/** What the form shows for a row not written yet: off, every country (the offer rule refuses a system with no row). */
const NOT_SET = { enabled: false, allowed_countries: null };

/**
 * An enablement read. A system staff have not switched on for the Merchant has no row (there is no tenant default,
 * BM-70 D-46), and its read answers 404: a valid state, not an error, so it renders `notSet` (whose Save writes the
 * row with the same PUT) instead of the error. Any other failure stays one.
 */
function EnablementBody({
    query,
    title,
    notSet,
    children,
}: {
    query: ReturnType<typeof useOne<MerchantPaymentSystem>>;
    title: string;
    notSet: ReactNode;
    children: (row: MerchantPaymentSystem) => ReactNode;
}) {
    if (query.isError && ApiError.is(query.error) && query.error.status === 404)
        return <>{notSet}</>;
    return (
        <DetailBody query={query} title={title}>
            {children}
        </DetailBody>
    );
}

function NotSet({ testId, children }: { testId: string; children: ReactNode }) {
    return (
        <p className="text-muted-foreground text-sm" data-testid={testId}>
            {children}
        </p>
    );
}

/** Whether checkout offers the system to the Merchant now, else the exact reason of the offer rule (BM-70 § 10). */
function MerchantOffer({
    system,
    merchant,
}: {
    system: string;
    merchant: string;
}) {
    const query = useOne<PaymentSystemOffer[]>(
        `${BASE}/merchants/${encodeURIComponent(merchant)}/offer`,
    );
    const offer = query.data?.data.find((o) => o.payment_system === system);
    return (
        <p className="text-sm" data-testid={`merchant-offer-${system}`}>
            <span className="text-muted-foreground">Checkout: </span>
            {query.isError
                ? `The offer did not load: ${apiErrorMessage(query.error)}`
                : !offer
                  ? '…'
                  : // shown only once an account serves the system (MerchantProvider)
                    offerText(offer, { linked: true })}
        </p>
    );
}

/** The Merchant's effective row of a system: the Merchant's own switch, then staff's switch and country list. */
function MerchantRow({
    system,
    merchant,
}: {
    system: string;
    merchant: string;
}) {
    const path = `${BASE}/${system}/merchants/${encodeURIComponent(merchant)}`;
    const query = useOne<MerchantPaymentSystem>(path);
    return (
        <div data-testid={`merchant-${system}`}>
            <EnablementBody
                query={query}
                title="Merchant enablement"
                notSet={
                    <>
                        <NotSet testId={`merchant-${system}-not-set`}>
                            Not set yet: staff have not switched this system on
                            for the Merchant. Saving creates its row.
                        </NotSet>
                        <EnablementForm
                            key={`${merchant}-not-set`}
                            id={`merchant-${system}`}
                            row={NOT_SET}
                            path={path}
                            hint="Empty means every country"
                        />
                    </>
                }
            >
                {(row) => (
                    <>
                        <p
                            className="text-sm"
                            data-testid={`merchant-switch-${system}`}
                        >
                            <span className="text-muted-foreground">
                                The Merchant&apos;s own switch:{' '}
                            </span>
                            {row.disabled_by_merchant
                                ? 'Switched off by the Merchant'
                                : 'On'}
                        </p>
                        <EnablementForm
                            key={`${merchant}-${row.version}`}
                            id={`merchant-${system}`}
                            row={row}
                            path={path}
                            hint="Empty means every country"
                        />
                    </>
                )}
            </EnablementBody>
        </div>
    );
}

const accountPath = (provider: string, merchant: string) =>
    `/staff/providers/${encodeURIComponent(provider)}/merchants/${encodeURIComponent(merchant)}/account`;

/**
 * The merchant page's "Payment providers" section (BM-70 V-3, § 10): one block per provider. Staff link one verified
 * account of the provider with ONE request, and every payment system the provider carries uses it (one link per
 * Merchant and provider, D-39); the systems' switches show once the Merchant has its own link of the provider (there
 * is no tenant default, D-49), each with its stage beside it.
 */
export function MerchantPaymentProviders({ merchant }: { merchant: string }) {
    const catalogue = useOne<Catalogue>(BASE);
    return (
        <Card data-testid="merchant-payment-providers">
            <CardHeader>
                <CardTitle className="text-base">Payment providers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                <DetailBody query={catalogue} title="Payment providers">
                    {(c) =>
                        c.providers.map((p) => (
                            <MerchantProvider
                                key={p.code}
                                provider={p}
                                stages={stagesOf(c, p.code)}
                                merchant={merchant}
                            />
                        ))
                    }
                </DetailBody>
            </CardContent>
        </Card>
    );
}

/** One provider of the Merchant: Link account first, then each system the provider carries, once an account serves it. */
function MerchantProvider({
    provider,
    stages,
    merchant,
}: {
    provider: Provider;
    stages: readonly PaymentSystemStage[];
    merchant: string;
}) {
    const accounts = useOne<ProviderAccount[]>(ACCOUNTS);
    const usages = useOne<ProviderAccountUsage[]>(BINDINGS);
    const [picked, setPicked] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const systems = stages.map((r) => r.payment_system);
    // one link per Merchant and provider: one request, whatever systems the provider carries (D-39)
    const link = useWrite(
        (provider_account_id: number) =>
            api().put<ProviderAccountBinding>(
                accountPath(provider.code, merchant),
                { provider_account_id },
            ),
        [ACCOUNTS, BASE],
    );
    const pool = accounts.data?.data ?? [];
    const live = usages.data?.data ?? [];
    // the link every system of the provider routes through: the Merchant's own only (D-49)
    const servedBy = live.find(
        (u) => u.provider === provider.code && u.merchant_id === merchant,
    );
    const servedAccount = servedBy
        ? accountOf(pool, servedBy.provider_account_id)
        : undefined;
    const linked = servedBy
        ? servedAccount
            ? servedAccount.name
            : `#${servedBy.provider_account_id}`
        : null;
    // only a verified, enabled account of this provider can serve a charge, so only those are offered (§ 8)
    const options = pool
        .filter((a) => a.provider === provider.code && a.status === 'verified')
        .map((a) => ({ value: String(a.id), label: a.name }));
    const pickId = `account-pick-${provider.code}`;
    return (
        <section
            className="space-y-4"
            data-testid={`merchant-provider-${provider.code}`}
        >
            <h3 className="text-sm font-semibold">{provider.display_name}</h3>
            {!systems.length ? (
                <p className="text-muted-foreground text-sm">
                    This provider carries no payment system yet.
                </p>
            ) : (
                <>
                    <p
                        className="text-sm"
                        data-testid={`linked-${provider.code}`}
                    >
                        <span className="text-muted-foreground">
                            Linked account:{' '}
                        </span>
                        {usages.isError
                            ? `The links did not load: ${apiErrorMessage(usages.error)}`
                            : !usages.data
                              ? '…'
                              : (linked ?? 'None yet')}
                    </p>
                    {accounts.isError ? (
                        <p role="alert" className="text-destructive text-sm">
                            The accounts did not load:{' '}
                            {apiErrorMessage(accounts.error)}
                        </p>
                    ) : null}
                    <form
                        method="post"
                        className="flex flex-col gap-2 md:flex-row md:items-end"
                        onSubmit={(e: FormEvent) => {
                            e.preventDefault();
                            if (!picked) return;
                            setError(null);
                            link.mutate(Number(picked), {
                                onSuccess: () => setPicked(null),
                                onError: (err) =>
                                    setError(apiErrorMessage(err)),
                            });
                        }}
                    >
                        <Field
                            id={pickId}
                            label="Link account"
                            hint={`Linked on every payment system this provider carries: ${systems.map(paymentSystemLabel).join(', ')}. Only verified accounts are listed.`}
                        >
                            <SearchableSelect
                                id={pickId}
                                label="Account"
                                testId={pickId}
                                options={options}
                                value={picked}
                                onChange={setPicked}
                            />
                        </Field>
                        <Button
                            type="submit"
                            disabled={!picked || link.isPending}
                        >
                            Link account
                        </Button>
                    </form>
                    {error ? (
                        <p role="alert" className="text-destructive text-sm">
                            {error}
                        </p>
                    ) : null}
                    {stages.map((r) => (
                        <div
                            key={r.payment_system}
                            className="space-y-2 rounded-md border p-3"
                            data-testid={`merchant-system-${r.payment_system}`}
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <h4 className="text-sm font-medium">
                                    {paymentSystemLabel(r.payment_system)}
                                </h4>
                                <Badge
                                    variant={STAGE_TONE[r.stage]}
                                    data-testid={`merchant-stage-${r.payment_system}`}
                                >
                                    {codeLabel(r.stage)}
                                </Badge>
                            </div>
                            {usages.data && !servedBy ? (
                                <p className="text-muted-foreground text-sm">
                                    Link an account to switch this payment
                                    system on.
                                </p>
                            ) : usages.data ? (
                                <>
                                    <MerchantOffer
                                        system={r.payment_system}
                                        merchant={merchant}
                                    />
                                    <MerchantRow
                                        system={r.payment_system}
                                        merchant={merchant}
                                    />
                                </>
                            ) : null}
                        </div>
                    ))}
                </>
            )}
        </section>
    );
}

/** `enabled` and the country list, saved together in one PUT by the Save button (spec § 9). */
function EnablementForm({
    id,
    row,
    path,
    hint,
}: {
    id: string;
    row: Pick<MerchantPaymentSystem, 'enabled' | 'allowed_countries'>;
    path: string;
    hint: string;
}) {
    const [enabled, setEnabled] = useState(row.enabled);
    const [countries, setCountries] = useState(
        row.allowed_countries?.join(', ') ?? '',
    );
    const [invalid, setInvalid] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const save = useWrite(
        (body: { enabled: boolean; allowed_countries: string[] | null }) =>
            api().put<MerchantPaymentSystem>(path, body),
        [BASE],
    );
    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        const allowed = parseCountries(countries);
        setInvalid(allowed === undefined);
        if (allowed === undefined) return;
        save.mutate(
            { enabled, allowed_countries: allowed },
            { onError: (err) => setError(apiErrorMessage(err)) },
        );
    };
    return (
        <form method="post" className="space-y-3" onSubmit={onSubmit}>
            <div className="flex items-center gap-2">
                <Switch
                    id={`${id}-enabled`}
                    checked={enabled}
                    onCheckedChange={setEnabled}
                />
                <label htmlFor={`${id}-enabled`} className="text-sm">
                    Enabled
                </label>
            </div>
            <Field
                id={`${id}-countries`}
                label={
                    <span className="inline-flex items-center gap-1">
                        Allowed countries
                        <LabelInfo label="About allowed countries">
                            Checked against the country of the buyer&apos;s
                            billing address, not their IP address. Empty means
                            every country.
                        </LabelInfo>
                    </span>
                }
                hint={hint}
                error={invalid ? COUNTRIES_ERROR : undefined}
            >
                <Input
                    id={`${id}-countries`}
                    placeholder="US, CA"
                    value={countries}
                    aria-invalid={invalid}
                    onChange={(e) => setCountries(e.target.value)}
                />
            </Field>
            {error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
            <Button type="submit" disabled={save.isPending}>
                Save
            </Button>
        </form>
    );
}
